<template>
  <div data-testid="page-insights-hub">
    <PageBreadcrumbs :items="breadcrumbs" />
    <div ref="stickySentinel" class="relative -top-16 mt-4 -mb-px h-px laptop:top-0" aria-hidden="true" />
    <!-- eslint-disable vue/no-restricted-v-bind -- same #EFF1F3 page ground as App.vue; no semantic surface token matches it (REV-903) -->
    <div
      class="sticky top-16 z-10 border-b laptop:top-0"
      :class="isStuck ? 'insights-hub-nav-stuck border-strong' : 'border-transparent'"
      :style="{ background: '#EFF1F3' }"
      data-testid="insights-hub-nav"
    >
      <!-- eslint-enable vue/no-restricted-v-bind -->
      <div class="-mb-px border-b border-main" data-testid="insights-hub-tabs">
        <TabBar>
          <NavTabBarItem
            v-for="tab in tabs"
            :key="tab.perspective"
            :is-selected="route.name === tab.routeName"
            :to="{ name: tab.routeName, params: { productId: product.id }, query: route.query }"
            :data-testid="`insights-tab-${tab.perspective}`"
          >
            {{ t(`Reviews.product.insights.perspectives.${tab.perspective}`) }}
          </NavTabBarItem>
          <TabBarItem v-for="tab in UPCOMING_TABS" :key="tab" disabled class="border-transparent" :data-testid="`insights-tab-${tab}`">
            {{ t(`Reviews.product.insights.perspectives.${tab}`) }}
          </TabBarItem>
        </TabBar>
      </div>
      <div class="flex flex-wrap items-center gap-2 py-2" data-testid="insights-hub-filters">
        <InsightsCategoryFilter v-if="hasCategoryFilter" :product="product" />
        <InsightsTimeframeFilter />
      </div>
    </div>
    <div class="mt-6">
      <RouterView :product="product" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs'
import type { ProductById } from '@/gql/reviews'
import { ReviewsInsightsPerspective, ReviewsRouteName } from '@/router/types'
import { useRoleRedirect } from '@/services/roleRedirect'
import { NavTabBarItem, TabBar, TabBarItem } from '@/ui/Tabs'

import InsightsCategoryFilter from './InsightsCategoryFilter.vue'
import InsightsTimeframeFilter from './InsightsTimeframeFilter.vue'

const props = defineProps<{ product: ProductById }>()

useRoleRedirect('manageReviews')

const { t } = useI18n()
const route = useRoute()
const breadcrumbs = useReviewsProductBreadcrumbs(props.product)

const tabs = [
  { perspective: ReviewsInsightsPerspective.Market, routeName: ReviewsRouteName.ReviewsProductInsightsMarket },
  { perspective: ReviewsInsightsPerspective.Competition, routeName: ReviewsRouteName.ReviewsProductInsightsCompetition },
  { perspective: ReviewsInsightsPerspective.Buyer, routeName: ReviewsRouteName.ReviewsProductInsightsBuyer },
  { perspective: ReviewsInsightsPerspective.Reviews, routeName: ReviewsRouteName.ReviewsProductInsightsReviews },
]

const stickySentinel = useTemplateRef<HTMLDivElement>('stickySentinel')
const isStuck = ref(false)

useIntersectionObserver(stickySentinel, ([entry]) => {
  isStuck.value = !entry.isIntersecting && entry.boundingClientRect.top < 0
})

const UPCOMING_TABS = ['intent', 'aiVisibility']

const CATEGORY_FILTER_ROUTES: unknown[] = [
  ReviewsRouteName.ReviewsProductInsightsMarket,
  ReviewsRouteName.ReviewsProductInsightsCompetition,
]
const hasCategoryFilter = computed(() => CATEGORY_FILTER_ROUTES.includes(route.name))
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.insights-hub-nav-stuck {
  border-image-source: linear-gradient(var(--border-color-strong) 0 0);
  border-image-slice: 0 0 1;
  border-image-width: 0 0 1px;
  border-image-outset: 0 100vw;

  @variant laptop {
    border-image-outset: 0 100vw 0
      calc((100vw - --spacing(64) - min(--theme(--container-6xl), 100vw - --spacing(64))) / 2 + --spacing(10) + --spacing(6));
  }
}
</style>
